import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { Observable, ReplaySubject, of, zip } from 'rxjs';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { SynchronizationEventService } from 'src/app/modules/synchronization/services/synchronization-event.service';
import { getPrettyZCStatus } from 'src/app/shared/enums/zoning-code-status';
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import { ZC_RELATED_TASKS } from '../../constants';
import { ZoningCode } from '../../models/zoning-code';
import { TaskService } from '../../services/task.service';
import { ZoningCodeService } from '../../services/zoning-code.service';
import IZoningCodeRecord = ZoningAPI.IZoningCodeRecord;
import { ISystemEvent, IZoningCodeEventMetadata } from 'src/app/shared/interfaces/system-event.interface';
import { SystemEventService } from '../../services/system-event.service';
import { SystemEventParentType } from 'src/app/shared/enums/system-event-parent-type';
import { EventsStatComponentComponent } from '../processes/sections/events-stat-component/events-stat-component.component';

@Component({
    standalone: false,
    selector: 'app-zoning-code-view',
    templateUrl: './zoning-code-view.component.html',
    styleUrls: ['./zoning-code-view.component.scss']
})
export class ZoningCodeViewComponent implements OnInit, OnDestroy {
    zoningCode: ZoningCode;
    tasks: ITaskModel[] = [];
    archivedTasks: ITaskModel[] = [];
    showSpinner: boolean = false;
    zoningCodeRecord: IZoningCodeRecord;
    getPrettyZCStatus = getPrettyZCStatus;
    stateEnv: string;
    private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    eventsList: ISystemEvent<IZoningCodeEventMetadata>[] = [];

    systemEventParentType = SystemEventParentType;

    @ViewChild('calcEventsStatistic') calcEventsStatistic: EventsStatComponentComponent;
    @ViewChild('indexEventsStatistic') indexEventsStatistic: EventsStatComponentComponent;

    constructor(
        private readonly taskService: TaskService,
        private readonly activateRoute: ActivatedRoute,
        private readonly zoningCodeService: ZoningCodeService,
        private readonly syncEventService: SynchronizationEventService,
        private readonly systemEventService: SystemEventService
    ) {
        this.taskService.setIsContainsProcessesForInstance(true);
    }

    get zoningCodeId(): number {
        return _.get(this, 'zoningCode.Id');
    }

    ngOnInit(): void {
        this.showSpinner = true;
        zip(this.activateRoute.paramMap, this.activateRoute.queryParamMap)
            .pipe(
                take(1),
                switchMap(([params, queryParams]) => {
                    const zoningCodeId = +params.get('id');
                    const stateEnv = queryParams.get('state_env');
                    this.stateEnv = stateEnv;
                    return this.zoningCodeService.getZoningCodesByIds([
                        {
                            Id: zoningCodeId,
                            State: stateEnv
                        }
                    ]);
                }),
                switchMap(zoningCodes => {
                    this.zoningCode = !_.isEmpty(zoningCodes) ? zoningCodes[0] : null;
                    return zip(
                        this.taskService.searchTasks({
                            zoningCodeId: this.zoningCodeId,
                            state_env: this.stateEnv,
                            type: ZC_RELATED_TASKS
                        }),
                        this.taskService.searchTasks({
                            zoningCodeId: this.zoningCodeId,
                            state_env: this.stateEnv,
                            type: ZC_RELATED_TASKS,
                            getArchived: true
                        }),
                        this.zoningCodeService.uploadVersionsForZoningCode(this.zoningCode, this.stateEnv),
                        this.zoningCodeService.getZoningCodeRecordById(this.zoningCodeId, this.stateEnv),
                        this.getSystemEvents()
                    );
                }),
                take(1),
                tap(([tasks, archivedTasks, versions, zoningCodeRecord]) => {
                    this.tasks = tasks;
                    this.archivedTasks = archivedTasks;
                    versions.forEach(version => version.updateTasksFromList(this.tasks));
                    this.zoningCodeRecord = zoningCodeRecord;
                })
            )
            .subscribe(() => {
                this.showSpinner = false;
            });
        this.syncEventService.updatedProcessesSubject.pipe(takeUntil(this.destroyed$)).subscribe(processDTOs => {
            if (_.isEmpty(processDTOs)) {
                return;
            }
            this.taskService.updateProcessesWithJSON(this.tasks, processDTOs);
            this.taskService.updateProcessesWithJSON(this.archivedTasks, processDTOs);
        });

        this.syncEventService.deletedProcessesSubject.pipe(takeUntil(this.destroyed$)).subscribe(processDTOs => {
            if (_.isEmpty(processDTOs)) {
                return;
            }
            this.tasks = this.taskService.getExistingProcesses(this.tasks, processDTOs);
            this.archivedTasks = this.taskService.getExistingProcesses(this.archivedTasks, processDTOs);
        });
        this.taskService.processes.pipe(takeUntil(this.destroyed$)).subscribe(tasks => {
            if (_.get(this, 'zoningCode.Versions')) {
                this.zoningCode.Versions.forEach(version => version.updateTasksFromList(tasks));
                this.tasks = tasks.filter(task => task.zoningCodeId === this.zoningCodeId);
            }
        });
    }

    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    getSystemEvents(): Observable<void> {
        if (!this.stateEnv || !this.zoningCodeId) {
            return of(null);
        }

        return this.systemEventService
            .getSystemEvents(
                this.stateEnv.toLocaleLowerCase(),
                String(this.zoningCodeId),
                SystemEventParentType.ZONING_CODE
            )
            .pipe(
                map((data: ISystemEvent<IZoningCodeEventMetadata>[]) => {
                    this.eventsList = data;
                })
            );
    }

    updateEventsList(data: ISystemEvent<IZoningCodeEventMetadata>[]) {
        if (_.isEmpty(data)) {
            return;
        }
        this.eventsList = data;
        if (this.calcEventsStatistic) {
            this.calcEventsStatistic.runUpdate();
        }
        if (this.indexEventsStatistic) {
            this.indexEventsStatistic.runUpdate();
        }
    }

    convertStatisticByStatusArray(statisticByStatuses: number[]) {
        if (_.isEmpty(statisticByStatuses)) {
            return [];
        }

        return statisticByStatuses
            .map((count, status) => {
                return count ? { status, count } : null;
            })
            .filter(el => el);
    }
}
